<template>
  <form v-on:submit.prevent="submit" v-on:change="changeHandler">
    <fieldset
      :disabled="readonly"
      style="border:0;padding:0;margin:0;min-width:0;"
    >
      <div class="card-body">
        <div class="card-body-overlay" v-if="!hasThirdParty">
          Please complete the "Third Party Details" section before the "Third
          Party Vehicle" section.
        </div>

        <div class="float-right" v-if="!readonly">
          <form-button :indicator="indicator" v-on:clicked="submit">
            Save
          </form-button>
        </div>

        <div class="clearfix"></div>
        <hr />

        <div class="row">
          <div class="col-lg-6">
            <vehicle-lookup
              v-on:vrm="setRegistration"
              v-on:vehicle="setVehicle"
              label="VRN"
              helper="Please enter the vehicle registration number for the third party."
              :registration="details.registration"
            ></vehicle-lookup>

            <selectlist
              :options="types"
              v-model="details.type_id"
              label="Vehicle Type"
              name="vehicle_type"
              helper="Please select the type of vehicle for the third party."
              placeholder="Please Select"
            ></selectlist>

            <selectlist
              :options="makes"
              v-model="details.make_id"
              label="Vehicle Make"
              placeholder="Please Select"
              helper="Please select the make of vehicle for the third party"
              name="make"
            ></selectlist>

            <inputbox
              v-model="details.model"
              name="model"
              label="Vehicle Model"
              helper="Please enter the vehicle model for the third party"
            ></inputbox>

            <selectlist
              :options="transmissions"
              v-model="details.transmission_id"
              name="transmission"
              helper="Please enter the transmission type for the third party"
              placeholder="Please Select"
              label="Vehicle Transmission"
            ></selectlist>
          </div>
          <div class="col-lg-6">
            <div
              class="form-group"
              :class="{ 'has-error': details.errors.has('registered_at') }"
            >
              <div class="col-md-4">
                <label for="registered_at" class="control-label"
                  >Registration Date:</label
                >
                <p class="help-block">
                  Please enter the vehicle registration date for the third party
                </p>
              </div>
              <div class="col-lg-8">
                <div class="input-group">
                  <datepicker
                    :disabled="disabled"
                    format="dd/MM/yyyy"
                    name="registration_date"
                    v-bind:value="details.registered_at"
                    input-class="form-control"
                    v-on:selected="setRegistrationDate"
                    v-on:input="changeHandler"
                    placeholder="dd/mm/yyyy"
                  ></datepicker>
                  <span
                    class="text-danger"
                    v-text="details.errors.get('registered_at')"
                    v-if="details.errors.has('registered_at')"
                  ></span>
                  <div class="input-group-append">
                    <span class="input-group-text">
                      {{ age }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <inputbox
              v-model="details.colour"
              name="vehicle_colour"
              label="Vehicle Colour"
              helper="Please enter the vehicle colour for the third party"
            ></inputbox>

            <inputbox
              v-model="details.engine_capacity"
              name="engine_capacity"
              label="Engine Capacity"
              helper="Please enter the engine capacity for the third party"
            ></inputbox>
          </div>

          <div class="col-lg-12">
            <input-block
              v-model="details.description_of_driver"
              name="description_of_driver"
              label="Description Of Driver"
              helper="Add description of driver here"
            ></input-block>
          </div>
        </div>

        <div class="col-lg-12">
          <fieldset>
            <legend>
              Damage Details
              <small>- Enter the damage details for the vehicle here</small>
            </legend>

            <vehicle-damage
              :readonly="readonly"
              :type="details.type_id"
              :damage="thirdPartyDamage"
              ref="vehicleDamage"
            ></vehicle-damage>
          </fieldset>
        </div>
      </div>
      <div class="card-footer" v-if="!readonly">
        <div class="float-left">
          <button
            type="button"
            class="btn btn-danger"
            @click="emitRemoveThirdParty"
          >
            Remove Third Party
          </button>
        </div>

        <div class="float-right">
          <form-button :indicator="indicator" v-on:clicked="submit">
            Save
          </form-button>
        </div>

        <div class="clearfix"></div>
      </div>
    </fieldset>
  </form>
</template>

<script>
import VehicleLookup from './../../../Generic/VehicleLookup.vue';
import VehicleDamage from './../../Shared/VehicleDamage';

export default {
  props: {
    incident: {
      type: String,
      required: true
    },
    thirdParty: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  components: {
    'vehicle-lookup': VehicleLookup,
    'vehicle-damage': VehicleDamage
  },

  data() {
    return {
      disabled: {
        from: new Date()
      },
      vehicle_identifier: null,
      damage_identifier: null,
      details: new window.Form({
        registration: null,
        type_id: null,
        make_id: null,
        model: null,
        transmission_id: null,
        registered_at: null,
        colour: null,
        engine_capacity: null,
        d_lock: false,
        tracker: false,
        disk_lock: false,
        alarm: false,
        grip_lock: false,
        immobiliser: false,
        armoured_chain: false,
        steering_lock: false,
        other: false,
        description_of_driver: null
      }),
      data_entered: false,
      indicator: false
    };
  },

  computed: {
    thirdPartyVehicle() {
      return this.thirdParty?.vehicle;
    },

    thirdPartyDamage() {
      return this.thirdParty?.vehicle?.damage;
    },

    types() {
      return this.$store.getters['VehicleTypes/all'];
    },

    makes() {
      return this.$store.getters['VehicleMakes/all'];
    },

    transmissions() {
      return this.$store.getters['VehicleTransmissions/all'];
    },

    age() {
      if (!this.details.registered_at) {
        return 'Please select a date';
      }

      let yearsOld = window.DateManager.differenceInYears(
        new Date(),
        this.details.registered_at
      );

      if (!yearsOld) {
        return 'Please select a date';
      }

      return yearsOld + ' Years ';
    },

    hasThirdParty() {
      if (!this.thirdParty) {
        return false;
      }

      return true;
    }
  },

  mounted() {
    this.mount();
  },

  methods: {
    mount() {
      if (this.hasThirdParty) {
        this.getThirdPartyVehicle();
      }
    },

    getThirdPartyVehicle() {
      if (
        this.thirdPartyVehicle &&
        Object.keys(this.thirdPartyVehicle).length
      ) {
        this.vehicle_identifier = this.thirdPartyVehicle.identifier;
        this.details.registration = this.thirdPartyVehicle.registration;
        this.details.type_id = this.thirdPartyVehicle.type_id;
        this.details.make_id = this.thirdPartyVehicle.make_id;
        this.details.model = this.thirdPartyVehicle.model;
        this.details.registered_at = this.thirdPartyVehicle.registered_at;
        this.details.colour = this.thirdPartyVehicle.colour;
        this.details.engine_capacity = this.thirdPartyVehicle.engine_capacity;
        this.details.transmission_id = this.thirdPartyVehicle.transmission_id;
        this.details.description_of_driver = this.thirdPartyVehicle.description_of_driver;
        this.damage_identifier = this.thirdPartyVehicle.identifier;
      }
    },

    /**
     * If the user tries to change tab when there are unsaved changes in the form we
     * want to display a popup warning them they have unsaved changes.
     *
     * To do that we add a form @change event listener to the form and then emit an
     * 'unsaved-data' event whenever a form value changes. This is then picked up
     * in the parent component and that handles displaying the warning if the user
     * tries to change tabs.
     */
    changeHandler() {
      if (!this.data_entered) {
        this.data_entered = true;
      }
      this.$emit('unsaved-data', this.data_entered);
    },

    submit() {
      if (this.readonly) return;
      window.toastify.info('Saving', "We're saving those details for you.");

      this.indicator = true;

      this.details[this.vehicle_identifier ? 'put' : 'post'](
        `/incidents/${this.incident}/third-parties/${this.thirdParty.identifier}/vehicle`
      )
        .then(response => {
          this.vehicle_identifier = response.identifier;
          this.indicator = false;
          this.saveDamageDetails();

          this.data_entered = false;
          this.$emit('unsaved-data', this.data_entered);
          this.$store.commit('Incident/setThirdPartyVehicle', {
            thirdParty: this.thirdParty,
            vehicle: response
          });
        })
        .catch(response => {
          this.indicator = false;
          window.toastify.error(
            'Error',
            'Sorry, there was an error saving the third party vehicle.'
          );
        });
    },

    saveDamageDetails() {
      if (this.vehicle_identifier && this.$refs.vehicleDamage.damageForm) {
        this.$refs.vehicleDamage.damageForm
          .post(
            `/incidents/${this.incident}/third-parties/${this.thirdParty.identifier}/vehicle/${this.vehicle_identifier}/damage`
          )
          .then(damage => {
            window.toastify.success(
              'Saved',
              "We've updated those details for you."
            );

            this.$store.commit('Incident/setThirdPartyVehicleDamage', {
              thirdParty: this.thirdParty,
              damage
            });
          })
          .catch(error => {});
      }
    },

    setRegistration(registration) {
      this.details.registration = registration;
    },

    setVehicle(vehicle) {
      this.details.transmission_id = vehicle.transmission_type_id;
      this.details.make_id = vehicle.make_id;
      this.details.model = vehicle.model;
      this.details.registered_at = window.DateManager.format(
        vehicle.registered_at,
        'DD/MM/YYYY'
      );
      this.details.colour = vehicle.colour;
      this.details.engine_capacity = vehicle.engine_capacity;
    },

    setRegistrationDate(value) {
      this.details.registered_at = value;
    },

    emitRemoveThirdParty() {
      this.$emit('remove-third-party');
    }
  },

  watch: {
    $route(to, from) {
      this.mount();
    }
  }
};
</script>
